import { useState } from "react";
import { sendContactMessage } from "../services/contactService";
import { downloadResume } from "../services/resumeService";

function SectionHeading({ number, children }) {
  return (
    <div className="section-head">
      <span className="tag">{number}</span>
      <h2>{children}</h2>
    </div>
  );
}

function yearsInWordsSince(startDate) {
  const today = new Date();
  const anniversary = new Date(`${startDate}T00:00:00`);
  let years = today.getFullYear() - anniversary.getFullYear();

  if (
    today.getMonth() < anniversary.getMonth() ||
    (today.getMonth() === anniversary.getMonth() &&
      today.getDate() < anniversary.getDate())
  ) {
    years -= 1;
  }

  const words = [
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
  ];

  return words[years] || String(years);
}

function yearsSince(startDate) {
  const today = new Date();
  const anniversary = new Date(`${startDate}T00:00:00`);
  let years = today.getFullYear() - anniversary.getFullYear();

  if (
    today.getMonth() < anniversary.getMonth() ||
    (today.getMonth() === anniversary.getMonth() &&
      today.getDate() < anniversary.getDate())
  ) {
    years -= 1;
  }

  return years;
}

function uptimeSince(startDate) {
  const today = new Date();
  const anniversary = new Date(`${startDate}T00:00:00`);
  let years = today.getFullYear() - anniversary.getFullYear();
  let months = today.getMonth() - anniversary.getMonth();

  if (today.getDate() < anniversary.getDate()) {
    months -= 1;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return `${years}yr ${months}mo`;
}

export function Hero() {
  const yearsInWords = yearsInWordsSince("2020-01-13");
  const capitalizedYearsInWords =
    yearsInWords.charAt(0).toUpperCase() + yearsInWords.slice(1);

  return (
    <section className="hero" id="top">
      <div className="eyebrow">/ USR / PRINCE_MOTSUMI</div>
      <h1>
        PRINCE
        <br />
        MOTSUMI
      </h1>
      <div className="role-line prompt">
        FULL&nbsp; STACK&nbsp; SOFTWARE&nbsp; ENGINEER
        <span className="cursor" />
      </div>
      <p className="subtext">
        I build things end to end — from schema to pixel.{" "}
        {capitalizedYearsInWords} years turning slow, tangled systems into fast,
        boring-in-a-good-way software. I work across the stack, from designing
        databases and APIs to building interfaces that feel simple and
        intentional.
      </p>
      <div className="cta-row">
        <a className="btn" href="#experience">
          VIEW_EXPERIENCE
        </a>
        <a className="btn ghost" href="#contact">
          SEND_MESSAGE
        </a>
      </div>
    </section>
  );
}

export function About() {
  return (
    <section id="about">
      <SectionHeading number="01">whoami</SectionHeading>
      <div className="about-box">
        <p>
          I'm a full stack engineer based between the frontend and the database,
          with a soft spot for the unglamorous middle layer nobody wants to own.
          I like systems that fail loudly, deploy quietly, and don't wake anyone
          up at 3AM.
        </p>
        <p>
          The road so far has taken me through legal and insurance tech. These
          days I build and evolve APIs and systems that handle the unglamorous
          but important stuff; before that, I spent 3 and a half years at a dev
          consultancy, working across multiple insurance platforms, mainly
          building policy generation flows, policy management dashboards,
          implementing major brand campaigns and rebrands, and integrating with
          external partners.
        </p>
        <p>
          Off-screen: Making music, playing around with tech, driving somewhere
          unfamiliar, and occasionally wondering why I started another project.
        </p>
      </div>
    </section>
  );
}

const skills = [
  ["Backend", 7],
  ["Frontend", 7],
  ["DevOps", 5],
  ["Databases", 7],
  ["Debugging patience", 10],
];

const specs = [
  ["OS", "Product-minded Engineer 3.2"],
  ["Uptime", `${yearsSince("2020-01-13")} years in the industry`],
  ["Languages", "TypeScript, Javascript, C#, Python, SQL"],
  ["Frontend", "Angular, React"],
  ["Backend", ".Net, FastAPI"],
  ["Data", "MSSQL, PostgreSQL"],
  ["Infra", "Docker, Azure, CI/CD"],
  ["CMS / Platforms", "Episerver (Optimizely CMS), Umbraco"],
];

export function Skills() {
  return (
    <section id="skills">
      <SectionHeading number="02">neofetch --skills</SectionHeading>
      <div className="neofetch">
        <pre className="ascii-machine">{`        .------------------.\n       /   AMBEX 9000       \\\n      /______________________\\\n      |  ┌────────────────┐  |\n      |  │ &gt; _             │  |\n      |  │                 │  |\n      |  └────────────────┘  |\n      |________________________|\n       \\______________________/\n        |    ||    |    ||    |\n        |    ||    |    ||    |\n       _|____||____|____||____|_\n      /_________________________\\`}</pre>
        <div className="specs">
          <dl>
            {specs.map(([term, value]) => (
              <div className="spec-row" key={term}>
                <dt>{term}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <div className="skill-list">
            {skills.map(([name, level]) => (
              <div className="skill-row" key={name}>
                <span className="name">{name}</span>
                <span className="meter" aria-label={`${level} out of 10`}>
                  {Array.from({ length: 10 }, (_, index) => (
                    <span
                      className={`blk ${index >= level ? "dim" : ""}`}
                      key={index}
                    >
                      {index >= level ? "░" : "█"}
                    </span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const jobs = [
  [
    "2023 — NOW",
    "Software Engineer III",
    "LexisNexis, South Africa",
    [
      `Contributed across the product's web application and supporting services,
      including custom document workflows, vendor-facing microservices APIs, and
      integrations with internal products`,
      `Contributed to an agentic workflow system using a human-in-the-loop approach,
      developing MCP tools and the microfrontend responsible for the human review stage`,
      `Built MCP integrations in front of existing APIs, allowing simpler operations to be 
      completed through an AI chat interface instead of lengthy traditional workflows`,
      `Participated in cross-team debugging and troubleshooting sessions, working across
      system boundaries to diagnose issues with integrated internal products`,
    ],
  ],
  [
    "2022 — 2023",
    "Intermediate SOftware Developer",
    "Retro Rabbit",
    [
      `Owned the end-to-end development of customer-facing policy generation flows 
      for car insurance, integrating the application journey with the insurer's APIs
       to generate policies. Alongside this, implemented major brand campaigns and 
       rebrands, and built external partner integrations including an end-to-end pet 
       insurance lead-generation flow`,
      "Developed site-wide enhancements, fixes, and maintenance work across five insurance brands",
    ],
  ],
  [
    "2020 — 2022",
    "Junior Software Developer",
    "Retro Rabbit",
    [
      `Contributed to the development of a digital platform for a major bank, 
      building scholarship application workflows, programme and event management tools,
      and an alumni portal designed to keep programme graduates connected and engaged`,
      `Contributed to the modernisation of policy management dashboards for an insurance
       company under the guidance of senior engineers`,
    ],
  ],
];

export function Experience() {
  return (
    <section id="experience">
      <SectionHeading number="04">history | grep career</SectionHeading>
      {jobs.map(([date, role, org, bullets]) => (
        <div className="log-entry" key={date}>
          <div className="ts">{date}</div>
          <div>
            <div className="role">{role}</div>
            <div className="org">{org}</div>
            <ul>
              {bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </section>
  );
}

export function Contact() {
  const [form, setForm] = useState({ from: "", subject: "", text: "" });
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [resumeStatus, setResumeStatus] = useState("");

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submitMessage = async (event) => {
    event.preventDefault();
    setSending(true);
    setStatus("");

    try {
      await sendContactMessage(form);
      setForm({ from: "", subject: "", text: "" });
      setStatus("MESSAGE ACCEPTED — TRANSMISSION COMPLETE");
    } catch (error) {
      setStatus(
        error.status === 429
          ? "RATE LIMIT EXCEEDED — TRY AGAIN IN AN HOUR"
          : "TRANSMISSION FAILED — TRY AGAIN LATER",
      );
    } finally {
      setSending(false);
    }
  };

  const handleResumeDownload = async (event) => {
    event.preventDefault();
    setResumeStatus("");

    try {
      await downloadResume();
    } catch {
      setResumeStatus("RESUME DOWNLOAD FAILED — TRY AGAIN LATER");
    }
  };

  return (
    <section id="contact">
      <SectionHeading number="05">sendmail --new</SectionHeading>
      <div className="contact-wrap">
        <form onSubmit={submitMessage}>
          <div className="field">
            <label htmlFor="fromField">FROM</label>
            <input
              id="fromField"
              type="email"
              placeholder="you@example.com"
              required
              value={form.from}
              onChange={updateField("from")}
            />
          </div>
          <div className="field">
            <label htmlFor="subjField">SUBJECT</label>
            <input
              id="subjField"
              type="text"
              placeholder="Let's build something"
              value={form.subject}
              onChange={updateField("subject")}
            />
          </div>
          <div className="field">
            <label htmlFor="bodyField">MESSAGE</label>
            <textarea
              id="bodyField"
              rows="5"
              placeholder="Type your message..."
              required
              value={form.text}
              onChange={updateField("text")}
            />
          </div>
          <button className="btn" type="submit" disabled={sending}>
            {sending ? "TRANSMITTING..." : "TRANSMIT →"}
          </button>
          {status ? <p role="status">{status}</p> : null}
        </form>
        <div className="contact-direct">
          <div className="comment"># direct channels</div>
          <a href="mailto:princelebogangmotsumi@gmail.com" className="prompt">
            mail princelebogangmotsumi@gmail.com
          </a>
          <br />
          <a
            href="https://github.com/Prince-Motsumi"
            target="_blank"
            rel="noreferrer"
            className="prompt"
          >
            open github.com/Prince-Motsumi
          </a>
          <br />
          <a
            href="https://www.linkedin.com/in/prince-lebogang-motsumi-072017192/"
            target="_blank"
            rel="noreferrer"
            className="prompt"
          >
            open linkedin.com/in/prince-lebogang-motsumi
          </a>
          <br />
          <button
            type="button"
            className="prompt resume-link"
            onClick={handleResumeDownload}
          >
            curl resume.pdf --output ./
          </button>
          {resumeStatus ? <p role="status">{resumeStatus}</p> : null}
        </div>
      </div>
    </section>
  );
}

export function Footer({ time }) {
  const uptime = uptimeSince("2020-01-13");

  return (
    <footer>
      <span>SYS_TIME {time}</span>
      <span>
        UPTIME: {uptime} | MEM: 64% CREATIVE_CAPACITY | STATUS:{" "}
        <strong>AVAILABLE_FOR_HIRE</strong>
      </span>
    </footer>
  );
}
